import React, { useState, useEffect } from 'react'
import Modal from 'react-modal'
import { UilTimes, UilExclamationCircle } from '@iconscout/react-unicons'
import InterviewContainer from './InterviewContainer'

// Single-question video-interview modal for the public "Try an assessment" trial flow.
// Deliberately reuses ModalTry.jsx's exact div structure (.modal-try > .modal-try-content >
// .video-question > InterviewContainer) so it inherits that component's existing CSS scaffold
// verbatim -- no new stylesheet needed.
const ModalTryInterview = ({ isOpen, onRequestClose, question, onSubmitAnswer, maxDurationSec }) => {
  const [recordedBlob, setRecordedBlob] = useState(null)
  const [isRecording, setIsRecording] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [cameraBlocked, setCameraBlocked] = useState(false)

  const resetLocalState = () => {
    setRecordedBlob(null)
    setIsRecording(false)
    setIsSubmitting(false)
    setSubmitError('')
    setCameraBlocked(false)
  }

  // Submission is already in flight when this fires -- closing just hides the modal,
  // it doesn't cancel the request -- so this is purely to stop users from thinking
  // they've lost their answer and re-recording unnecessarily.
  useEffect(() => {
    if (!isSubmitting) {
      return undefined
    }
    const handleBeforeUnload = (e) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isSubmitting])

  const handleClose = () => {
    if (isRecording && !window.confirm('Your recording is still in progress. Close anyway?')) {
      return
    }
    if (isSubmitting && !window.confirm('Your answer is still being submitted. Close anyway?')) {
      return
    }
    onRequestClose()
    resetLocalState()
  }

  const handleSaveAnswer = (e, currentQuestionIndex, questionId, type, blob) => {
    if (type === 'video') {
      setRecordedBlob(blob)
      submitAnswer(blob)
    }
  }

  // Auto-submits as soon as recording stops -- there's no separate "Submit Answer"
  // step, so this also doubles as the retry action if submission fails.
  const submitAnswer = (blob) => {
    setIsSubmitting(true)
    setSubmitError('')
    onSubmitAnswer(blob)
      .then(() => {
        resetLocalState()
      })
      .catch((error) => {
        setIsSubmitting(false)
        setSubmitError((error && error.message) || 'Something went wrong submitting your answer. Please try again.')
      })
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel="Try Interview Modal"
      ariaHideApp={false}
      shouldCloseOnOverlayClick={false}
      className="modal-try"
    >
      <div className="modal-try-container">
        <div className="modal-try-header">
          {!isRecording && !isSubmitting && (
            <button className="modal-close-btn" type="button" onClick={handleClose}>
              <UilTimes size={28} />
            </button>
          )}
        </div>

        <div className="modal-try-content">
          <h5>{question && question.get('title')}</h5>

          {cameraBlocked && (
            <p className="field-error">
              <UilExclamationCircle size={20} color="#E99F0C" /> Camera/microphone access is blocked. Please allow access in your browser settings and try again.
            </p>
          )}

          {question && (
            <div className="video-question">
              <InterviewContainer
                isOpen={isOpen}
                question={question}
                currentQuestionIndex={0}
                handleSaveAnswer={handleSaveAnswer}
                onError={() => setCameraBlocked(true)}
                onRecordingStateChange={setIsRecording}
                maxDurationSec={maxDurationSec}
              />
            </div>
          )}

          {isSubmitting && (
            <div className="recommends-submit-status">
              <i className="fa fa-spin fa-spinner" />
              Submitting your answer...
            </div>
          )}
          {submitError && <p className="field-error">{submitError}</p>}
        </div>

        <div className="modal-try-footer">
          <div className="footer-left" />
          <div className="footer-right">
            {submitError && (
              <button
                type="button"
                className="btn-primary"
                onClick={() => submitAnswer(recordedBlob)}
                data-ls-id="assess-retry-submit-interview-answer-button"
              >
                Try Again
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default ModalTryInterview
